import { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  translations?: {
    title: string;
    description: string;
    refreshPage: string;
    goBack: string;
    devDetails: string;
  };
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Log to external error reporting service in production
    if (import.meta.env.PROD) {
      // Example: Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use provided translations or fallback to English
      const t = this.props.translations || {
        title: 'Something went wrong',
        description: "We're sorry, but something unexpected happened. Please try refreshing the page.",
        refreshPage: 'Refresh Page',
        goBack: 'Go Back',
        devDetails: 'Error Details (Development Mode)'
      };

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-red-500" />
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                {t.title}
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t.description}
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="mt-6 text-left">
                  <details className="text-sm text-red-600 dark:text-red-400">
                    <summary className="cursor-pointer font-medium">
                      {t.devDetails}
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap text-xs">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </details>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-primary"
                >
                  {t.refreshPage}
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="btn btn-outline"
                >
                  {t.goBack}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to inject translations
export function ErrorBoundaryWithTranslations({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      translations={{
        title: t('errors.boundary.title'),
        description: t('errors.boundary.description'),
        refreshPage: t('errors.boundary.refreshPage'),
        goBack: t('errors.boundary.goBack'),
        devDetails: t('errors.boundary.devDetails'),
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;